import { useEffect, useState } from 'react'
import { Copy, Download, Share2 } from 'lucide-react'
import QRCode from 'qrcode'
import { toast } from 'sonner'

import { campaignUrl, shareIntents, utmLink, type ShareChannel } from '@iws/theme-schema/campaign'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface Props {
  domain: string
  slug: string
  title: string
  subtitle: string
  heroImage?: string
  accent?: string
}

const CHANNELS: { id: ShareChannel; label: string }[] = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
]

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    toast.success('Copied')
  } catch {
    toast.error('Could not copy — select the text and copy it instead')
  }
}

const download = (href: string, filename: string) => {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
}

/** Loads an image for the canvas; CDN images without CORS headers fail, and the card is drawn without them. */
const loadImage = (src: string) =>
  new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })

/** A 1200 × 630 social card: hero image (when it can be read), dark overlay, title and offer line. */
const drawSocialImage = async ({ title, subtitle, heroImage, accent }: Pick<Props, 'title' | 'subtitle' | 'heroImage' | 'accent'>) => {
  const canvas = document.createElement('canvas')
  canvas.width = 1200
  canvas.height = 630
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = accent || '#111827'
  ctx.fillRect(0, 0, 1200, 630)
  const img = heroImage ? await loadImage(heroImage) : null
  if (img) {
    const scale = Math.max(1200 / img.width, 630 / img.height)
    ctx.drawImage(img, (1200 - img.width * scale) / 2, (630 - img.height * scale) / 2, img.width * scale, img.height * scale)
    ctx.fillStyle = 'rgba(0,0,0,0.55)'
    ctx.fillRect(0, 0, 1200, 630)
  }
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 72px system-ui, sans-serif'
  const words = title.split(/\s+/)
  const lines: string[] = []
  for (const w of words) {
    const last = lines[lines.length - 1]
    if (last !== undefined && ctx.measureText(`${last} ${w}`).width < 1040) lines[lines.length - 1] = `${last} ${w}`
    else lines.push(w)
  }
  lines.slice(0, 3).forEach((l, i) => ctx.fillText(l, 80, 220 + i * 86))
  ctx.font = '36px system-ui, sans-serif'
  ctx.fillText(subtitle.slice(0, 70), 80, 220 + Math.min(lines.length, 3) * 86 + 30)
  try {
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

/** Link, UTM links, share buttons, QR code and a social image for a published campaign (FR-CB11). */
export const ShareKit = ({ domain, slug, title, subtitle, heroImage, accent }: Props) => {
  const url = campaignUrl(domain, slug)
  const intents = shareIntents(url, title, slug)
  const [qr, setQr] = useState<string | null>(null)

  // Scans are tagged like any other channel: utm_source=qr, utm_medium=offline.
  const qrUrl = (() => {
    const u = new URL(url)
    u.searchParams.set('utm_source', 'qr')
    u.searchParams.set('utm_medium', 'offline')
    u.searchParams.set('utm_campaign', slug)
    return u.toString()
  })()

  useEffect(() => {
    QRCode.toDataURL(qrUrl, { width: 512, margin: 2 })
      .then(setQr)
      .catch(() => setQr(null))
  }, [qrUrl])

  const downloadSvg = async () => {
    const svg = await QRCode.toString(qrUrl, { type: 'svg', margin: 2 })
    download(URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' })), `${slug}-qr.svg`)
  }

  const downloadSocial = async () => {
    const png = await drawSocialImage({ title, subtitle, heroImage, accent })
    if (!png) {
      toast.error('Could not create the image')
      return
    }
    download(png, `${slug}-social.png`)
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium text-ink">Campaign link</p>
        <div className="flex gap-2">
          <Input readOnly value={url} aria-label="Campaign link" onFocus={(e) => e.currentTarget.select()} />
          <Button variant="outline" onClick={() => copy(url)}><Copy />Copy</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline"><a href={intents.whatsapp} target="_blank" rel="noreferrer"><Share2 />WhatsApp</a></Button>
        <Button asChild variant="outline"><a href={intents.facebook} target="_blank" rel="noreferrer"><Share2 />Facebook</a></Button>
        <Button asChild variant="outline"><a href={intents.sms}><Share2 />SMS</a></Button>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-ink">Tracked links (UTM)</p>
        <ul className="space-y-1.5">
          {CHANNELS.map((c) => {
            const link = utmLink(url, c.id, slug)
            return (
              <li key={c.id} className="flex items-center gap-2 text-sm">
                <span className="w-20 shrink-0 text-muted-foreground">{c.label}</span>
                <code className="min-w-0 flex-1 truncate rounded-md bg-subdued px-2 py-1 text-xs text-ink">{link}</code>
                <Button size="icon" variant="ghost" aria-label={`Copy ${c.label} link`} onClick={() => copy(link)}><Copy /></Button>
              </li>
            )
          })}
        </ul>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <p className="text-sm font-medium text-ink">QR code (for posters and packaging)</p>
          {qr && <img src={qr} alt={`QR code for ${url}`} className="size-36 rounded-md border border-border bg-card" />}
          <div className="flex gap-2">
            <Button variant="outline" disabled={!qr} onClick={() => qr && download(qr, `${slug}-qr.png`)}><Download />PNG</Button>
            <Button variant="outline" onClick={downloadSvg}><Download />SVG</Button>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-ink">Social image (1200 × 630)</p>
          <p className="text-sm text-text-2">Made from the hero image, the title and the offer.</p>
          <Button variant="outline" onClick={downloadSocial}><Download />Download image</Button>
        </div>
      </div>
    </div>
  )
}
